/**
 * LearnRegression.jsx — user reads a passage at their own pace and
 * every scroll-up or "Re-read" tap is counted as a backtrack event.
 * The count is shown privately at the end (never as a penalty) —
 * awareness alone reduces the habit. Sessions with ≤ 2 backtracks
 * earn the "Smooth Reader" badge treatment in the results card.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Play, StopCircle, ArrowLeft, RotateCcw, Undo2, ChevronDown, Sparkles } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { readCustomPassage } from '../utils/learnCustomPassage';
import LearnQuiz from '../components/LearnQuiz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LearnRegression = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [passageIdx, setPassageIdx] = useState(0);
  const [state, setState] = useState('setup'); // setup|reading|quiz|done
  const [backtracks, setBacktracks] = useState(0);
  const startedAt = useRef(0);
  const durationRef = useRef(0);
  const scrollRef = useRef(null);
  const lastScrollTop = useRef(0);

  useEffect(() => {
    const custom = readCustomPassage();
    if (custom) { setPassages([custom]); return; }
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/learn/passages`);
        if (r.ok) { const d = await r.json(); setPassages(d.passages || []); }
      } catch { /* non-fatal */ }
    })();
  }, []);

  // Debounced scroll-up detection while reading. Uses a 40px threshold
  // so a jitter tap doesn't spam the counter.
  useEffect(() => {
    if (state !== 'reading' || !scrollRef.current) return;
    const el = scrollRef.current;
    lastScrollTop.current = el.scrollTop;
    const onScroll = () => {
      const cur = el.scrollTop;
      if (cur + 40 < lastScrollTop.current) {
        setBacktracks((b) => b + 1);
      }
      lastScrollTop.current = cur;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [state]);

  const passage = passages[passageIdx];
  const start = () => { setBacktracks(0); startedAt.current = Date.now(); setState('reading'); };
  const stop = () => { durationRef.current = Date.now() - startedAt.current; setState('quiz'); };
  const reset = () => { setState('setup'); setBacktracks(0); durationRef.current = 0; };

  if (!passage) {
    return (
      <main className="sharp-bg min-h-screen flex items-center justify-center text-white/60" data-testid="learn-regression-loading">
        Loading passages…
      </main>
    );
  }

  return (
    <main className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="learn-regression-page">
      <button onClick={() => navigate('/learn')} className="text-xs text-white/50 hover:text-white/80 mb-4 inline-flex items-center gap-1.5">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to LEARN
      </button>

      <header className="mb-6 flex items-center gap-3">
        <TrendingUp className="w-6 h-6 text-emerald-300" />
        <div>
          <p className="eyebrow text-emerald-300 mb-0.5">Drill · Regression tracking</p>
          <h1 className="font-display text-3xl font-black text-white tracking-tight leading-none">
            Smooth Reader<span className="text-emerald-300">.</span>
          </h1>
        </div>
      </header>

      {state === 'setup' && (
        <div className="space-y-5">
          <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5">
            <p className="eyebrow text-white/50 mb-2">Passage</p>
            <div className="relative">
              <select
                value={passageIdx}
                onChange={(e) => setPassageIdx(Number(e.target.value))}
                className="w-full appearance-none bg-black/40 border border-white/15 rounded-lg pl-3 pr-9 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-400/60"
                data-testid="learn-regression-passage-select"
              >
                {passages.map((p, i) => (<option key={p.passage_id} value={i}>{p.title} — {p.author} ({p.word_count} words)</option>))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            </div>
          </div>
          <p className="text-xs text-white/50 leading-relaxed">
            Read the passage at your natural pace. Every scroll upward
            counts as a backtrack — awareness alone reduces the habit.
            No penalties, just a private stat.
          </p>
          <button
            onClick={start}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-4 py-3.5 text-sm font-black tracking-tight transition-colors"
            data-testid="learn-regression-start"
          >
            <Play className="w-4 h-4" /> Start reading
          </button>
        </div>
      )}

      {state === 'reading' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-xl bg-black/40 border border-emerald-500/30 px-4 py-2.5">
            <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-black flex items-center gap-1.5">
              <Undo2 className="w-3.5 h-3.5" /> Backtracks
            </p>
            <p className="font-display text-3xl font-black text-white tabular-nums" data-testid="learn-regression-backtracks">
              {backtracks}
            </p>
          </div>
          <div
            ref={scrollRef}
            className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-950 to-black border border-emerald-500/20 p-6 text-white/90 leading-relaxed text-base sm:text-lg max-h-[55vh] overflow-y-auto"
            data-testid="learn-regression-passage-text"
          >
            {passage.text}
          </div>
          <button
            onClick={() => setBacktracks((b) => b + 1)}
            className="w-full text-xs text-emerald-300/80 hover:text-emerald-200 inline-flex items-center justify-center gap-1.5 py-1.5"
            data-testid="learn-regression-reread"
          >
            <Undo2 className="w-3.5 h-3.5" /> I re-read that sentence (log it)
          </button>
          <button
            onClick={stop}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-4 py-3.5 text-sm font-black tracking-tight transition-colors"
            data-testid="learn-regression-done"
          >
            <StopCircle className="w-4 h-4" /> Done — score me
          </button>
        </div>
      )}

      {state === 'quiz' && (
        <div className="space-y-4">
          {backtracks <= 2 && (
            <div className="rounded-xl bg-emerald-500/15 border border-emerald-400/40 px-4 py-3 flex items-center gap-2 text-emerald-200 text-sm font-black">
              <Sparkles className="w-4 h-4" /> Smooth Reader — {backtracks} backtrack{backtracks === 1 ? '' : 's'} this pass.
            </div>
          )}
          <p className="text-xs text-white/50">
            Backtracks logged: <span className="text-emerald-300 font-black tabular-nums">{backtracks}</span>
          </p>
          <LearnQuiz
            passage={passage}
            drillKind="regression"
            durationMs={durationRef.current}
            metadata={{ backtracks }}
            onDone={() => setState('done')}
          />
        </div>
      )}

      {state === 'done' && (
        <div className="mt-5 flex gap-3">
          <button onClick={reset} className="flex-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-4 py-3 text-sm font-black" data-testid="learn-regression-again">
            <RotateCcw className="w-4 h-4 inline mr-2" /> Run another drill
          </button>
          <button onClick={() => navigate('/learn')} className="rounded-lg bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 text-white/70 px-4 py-3 text-sm font-black">
            Back to LEARN
          </button>
        </div>
      )}
    </main>
  );
};

export default LearnRegression;
